import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router-dom";
import { axioTokens } from "../theme";
import markLight from "../assets/axiohire_only_logo.svg";
import markDark from "../assets/axiohire_only_logo_dark.svg";
import fullLight from "../assets/axiohire_with_logo_with_tagline.svg";
import fullDark from "../assets/axiohire_with_logo_with_tagline_dark.svg";
import wordmarkTaglineLight from "../assets/axiohire_without_logo_with_tagline.svg";
import wordmarkTaglineDark from "../assets/axiohire_without_logo_with_tagline_dark.svg";
import wordmarkLight from "../assets/axiohire_without_logo_without_tagline.svg";
import wordmarkDark from "../assets/axiohire_without_logo_without_tagline_dark.svg";

type LogoVariant = "full" | "lockup" | "wordmark" | "wordmarkTagline" | "mark";
type LogoScheme = "light" | "dark";

interface BrandLogoProps {
  /**
   * full            — icon + wordmark + tagline (auth / marketing heroes)
   * lockup          — icon + wordmark, no tagline
   * wordmark        — wordmark only (app headers)
   * wordmarkTagline — wordmark + tagline
   * mark            — icon only
   */
  variant?: LogoVariant;
  /** light = navy+purple on light UI; dark = white+purple on dark panels */
  scheme?: LogoScheme;
  /** Overall logo height in px */
  height?: number;
  /** Link target. Pass null for a non-clickable image. */
  to?: string | null;
  className?: string;
  style?: CSSProperties;
}

const SINGLE: Record<Exclude<LogoVariant, "lockup">, Record<LogoScheme, string>> = {
  full: { light: fullLight, dark: fullDark },
  wordmark: { light: wordmarkLight, dark: wordmarkDark },
  wordmarkTagline: { light: wordmarkTaglineLight, dark: wordmarkTaglineDark },
  mark: { light: markLight, dark: markDark },
};

function LogoImg({
  src,
  height,
  className,
  style,
}: {
  src: string;
  height: number;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <img
      src={src}
      alt="AxioHire"
      height={height}
      className={className}
      style={{
        display: "block",
        height,
        width: "auto",
        ...style,
      }}
    />
  );
}

export function BrandLogo({
  variant = "lockup",
  scheme = "light",
  height = axioTokens.unit[28],
  to = "/",
  className,
  style,
}: BrandLogoProps) {
  let content: ReactNode;

  if (variant === "lockup") {
    // Compose clean icon + wordmark (no tagline) for compact headers.
    // Mark is naturally taller (115×117) than the wordmark (335×71),
    // so the wordmark is optically centered against the mark.
    const markH = height;
    const wordH = Math.round(height * 0.62);
    content = (
      <span
        className={className}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: Math.max(8, Math.round(height * 0.28)),
          lineHeight: "var(--axio-number-zero)",
          ...style,
        }}
      >
        <LogoImg src={SINGLE.mark[scheme]} height={markH} />
        <LogoImg src={SINGLE.wordmark[scheme]} height={wordH} />
      </span>
    );
  } else {
    content = (
      <LogoImg src={SINGLE[variant][scheme]} height={height} className={className} style={style} />
    );
  }

  if (!to) return content;

  return (
    <Link
      to={to}
      aria-label="AxioHire home"
      style={{ display: "inline-flex", alignItems: "center", lineHeight: "var(--axio-number-zero)", textDecoration: "none" }}
    >
      {content}
    </Link>
  );
}
